import { Brain, Inbox, Lock, ArrowRight } from 'lucide-react'
import { CONTACT_SALES_EMAIL, track } from '@/lib/analytics'

/**
 * Enterprise capabilities section — fake-door validation for two
 * unbuilt features: private per-org fine-tuning and email-inbox intake.
 *
 * Both are positioned as "Available on Enterprise — talk to sales"
 * rather than as features on a buyable plan, so we get a sales
 * conversation (which reveals real demand) without misleading anyone
 * who upgrades expecting a toggle.
 *
 * Every CTA fires a distinct `enterprise_inquiry_click` event with a
 * `capability` param so we can see in GA4 which feature drove the
 * click. The `source` param distinguishes hero vs section clicks.
 *
 * We'll watch the click and demo-conversion data for 4–6 weeks. If a
 * meaningful share of homepage visitors click *and* mention the
 * capability on the sales call, we ship the feature. If not, we
 * either rewrite the pitch or move the section.
 */

function mailto(capability: string, label: string) {
  return `mailto:${CONTACT_SALES_EMAIL}?subject=${encodeURIComponent(`Enterprise interest: ${label}`)}`
}

export function EnterpriseCapabilities() {
  return (
    <section className="py-20 sm:py-28 bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 text-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/15 text-blue-300 text-xs font-medium mb-4 ring-1 ring-blue-400/30">
            <Lock size={14} />
            Enterprise capabilities
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">
            Built for teams that need more
          </h2>
          <p className="mt-4 text-base sm:text-lg text-slate-300">
            Two capabilities reserved for our Enterprise tier — designed for the
            volume, privacy, and integration needs of larger operations.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Card 1: Continuous fine-tuning */}
          <div className="rounded-2xl bg-white/[0.04] ring-1 ring-white/10 p-8 backdrop-blur-sm flex flex-col">
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-blue-500/15 ring-1 ring-blue-400/30 flex items-center justify-center">
                <Brain size={22} className="text-blue-300" />
              </div>
              <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-blue-500/15 text-blue-200 text-[11px] font-semibold tracking-wide uppercase ring-1 ring-blue-400/30">
                Enterprise
              </span>
            </div>
            <h3 className="text-xl font-semibold mb-3">
              Your AI gets smarter with every correction — privately
            </h3>
            <p className="text-sm text-slate-300 leading-relaxed mb-5">
              We fine-tune a model on the corrections your team makes. Your
              data never leaves your tenant. The resulting model is yours
              alone — competitors don't benefit from your accuracy gains.
            </p>
            <ul className="space-y-2 text-sm text-slate-300 mb-6 flex-1">
              <li className="flex items-start gap-2">
                <span className="text-blue-400 mt-0.5">→</span>
                Per-organization model trained on your <code className="text-slate-200 bg-white/[0.06] px-1 rounded text-xs">feedback_entries</code>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-400 mt-0.5">→</span>
                Accuracy compounds with usage — flat for every competitor
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-400 mt-0.5">→</span>
                Strict tenant isolation; auditable training data lineage
              </li>
            </ul>
            <a
              href={mailto('continuous_fine_tuning', 'Continuous fine-tuning')}
              onClick={() => track('enterprise_inquiry_click', { capability: 'continuous_fine_tuning', source: 'enterprise_section' })}
              className="inline-flex items-center gap-2 text-sm font-semibold text-blue-300 hover:text-blue-200 transition-colors"
            >
              Talk to sales about private fine-tuning
              <ArrowRight size={16} />
            </a>
          </div>

          {/* Card 2: Email intake */}
          <div className="rounded-2xl bg-white/[0.04] ring-1 ring-white/10 p-8 backdrop-blur-sm flex flex-col">
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/15 ring-1 ring-emerald-400/30 flex items-center justify-center">
                <Inbox size={22} className="text-emerald-300" />
              </div>
              <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-blue-500/15 text-blue-200 text-[11px] font-semibold tracking-wide uppercase ring-1 ring-blue-400/30">
                Enterprise
              </span>
            </div>
            <h3 className="text-xl font-semibold mb-3">
              Forward an invoice. See it extracted in 30 seconds.
            </h3>
            <p className="text-sm text-slate-300 leading-relaxed mb-5">
              Point a shared mailbox at DocumentIQ. Attachments auto-ingest,
              get processed, and land in your queue — or push straight to
              your ERP. No upload step, no behaviour change for the people
              already in the email loop.
            </p>
            <ul className="space-y-2 text-sm text-slate-300 mb-6 flex-1">
              <li className="flex items-start gap-2">
                <span className="text-emerald-400 mt-0.5">→</span>
                Dedicated inbound address per project (or forward from your own)
              </li>
              <li className="flex items-start gap-2">
                <span className="text-emerald-400 mt-0.5">→</span>
                Auto-extracts PDFs, DOCs, and inline body text
              </li>
              <li className="flex items-start gap-2">
                <span className="text-emerald-400 mt-0.5">→</span>
                Webhook + ERP push so extracted data lands where you need it
              </li>
            </ul>
            <a
              href={mailto('email_intake', 'Email inbox intake')}
              onClick={() => track('enterprise_inquiry_click', { capability: 'email_intake', source: 'enterprise_section' })}
              className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-300 hover:text-emerald-200 transition-colors"
            >
              Talk to sales about email intake
              <ArrowRight size={16} />
            </a>
          </div>
        </div>

        {/* Footer line — single CTA for "both" */}
        <div className="mt-10 text-center">
          <a
            href={mailto('both', 'Enterprise plan')}
            onClick={() => track('enterprise_inquiry_click', { capability: 'both', source: 'enterprise_section_footer' })}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-white text-slate-900 text-sm font-semibold hover:bg-slate-100 transition-colors"
          >
            Book a 30-minute Enterprise scoping call
            <ArrowRight size={16} />
          </a>
          <p className="mt-3 text-xs text-slate-400">
            Custom SLAs, dedicated support, and other Enterprise capabilities also covered.
          </p>
        </div>
      </div>
    </section>
  )
}
